import { PageLoader } from '@/components/PageLoader';
import { useMemo, useState } from 'react';

import { bookingsKey } from '@/api/bookings';
import { apiFetch } from '@/api/client';
import {
  disputesKey,
  openDisputesKey,
  useBookingDisputes,
  useOpenDisputes,
  type Dispute,
  type RaiseDisputeRequest,
  type ResolveDisputeRequest,
} from '@/api/disputes';
import { useApiMutation } from '@/api/mutations';
import { DataView, type DataViewColumn } from '@/components/DataView';
import {
  Button,
  Card,
  EmptyState,
  Notice,
  PageHeading,
  SectionTitle,
} from '@/components/PageHeading';
import { StatusBadge } from '@/components/StatusBadge';
import { WriteAction } from '@/components/WriteAction';
import { formatDate, formatDateTime } from '@core/format/dates';
import { formatAmount } from '@core/format/money';
import type { Session } from '@core/session/types';
import { RaiseDisputeDrawer } from './disputes/RaiseDisputeDrawer';
import { ResolveDisputeDrawer } from './disputes/ResolveDisputeDrawer';
import { needsChargeCorrection, statusTone, summarise } from './disputes/dispute';

/**
 * Section 9's dispute log — the argument, written down while both sides still remember it.
 *
 * <p>
 * <strong>Two permissions, and they genuinely differ.</strong> Recording that a customer objected
 * is `ReturnInspection:Edit`, because anyone who may bill somebody should be able to write down
 * that they complained. Closing the argument is `DepositSettlement:Approve`, which the Accountant
 * role holds and the counter does not. So the screen is visible to the counter, the raise button is
 * theirs, and the resolve button is not — rather than one gate hiding the log from the people who
 * fill it in.
 * </p>
 *
 * <p>
 * The list is what is still open. History is per booking, reached by picking a row — because "every
 * dispute this shop has ever had" is a report, and what a person standing at a counter needs is the
 * one conversation in front of them.
 * </p>
 */
export function DisputesPage({ session }: { session: Session }) {
  const [raising, setRaising] = useState<{ bookingId: number | null } | null>(null);
  const [resolving, setResolving] = useState<Dispute | null>(null);
  const [history, setHistory] = useState<Dispute | null>(null);

  const open = useOpenDisputes();
  const forBooking = useBookingDisputes(history?.bookingId ?? null);

  const rows = useMemo(() => open.data ?? [], [open.data]);
  const summary = useMemo(() => summarise(rows), [rows]);

  // A dispute changes nothing about the money, so nothing else needs invalidating — except the
  // booking, whose own view lists them.
  const invalidates = [openDisputesKey, disputesKey, bookingsKey];

  const raise = useApiMutation<Dispute, RaiseDisputeRequest>({
    mutationFn: (body) => apiFetch<Dispute>('/api/dispute', { method: 'POST', body }),
    success: 'Recorded. It stays open until somebody closes it.',
    invalidates,
    onSuccess: () => setRaising(null),
  });

  const resolve = useApiMutation<Dispute, ResolveDisputeRequest>({
    mutationFn: (body) =>
      apiFetch<Dispute>(`/api/dispute/${resolving?.id}/resolve`, { method: 'POST', body }),
    success: (result) => `Closed as ${result.status.toLowerCase()}.`,
    invalidates,
    onSuccess: () => setResolving(null),
  });

  const columns: DataViewColumn<Dispute>[] = [
    {
      title: 'Customer',
      dataIndex: 'customerName',
      primary: true,
      render: (_, row) => (
        <div>
          <div className="text-sm font-medium text-gray-900">{row.customerName}</div>
          <div className="font-mono-nums text-[13px] text-default">
            {row.bookingNumber ?? 'no booking number'}
            {/* Null means the settlement as a whole, which is the commoner case — so it is said in
                words rather than left as a blank column somebody reads as missing data. */}
            {row.garmentName ? ` · ${row.garmentName}` : ' · whole settlement'}
          </div>
        </div>
      ),
    },
    {
      title: 'What they say',
      dataIndex: 'claim',
      render: (_, row) => (
        <span className="block max-w-[36ch] truncate text-[13px] text-default" title={row.claim}>
          {row.claim}
        </span>
      ),
    },
    {
      title: 'In question',
      dataIndex: 'disputedAmount',
      align: 'right',
      render: (_, row) => (
        <span className="font-mono-nums text-[13px]">
          {/* Null is a real answer — an objection with no figure attached — and a zero would read
              as an argument about nothing. */}
          {row.disputedAmount === null ? 'not stated' : formatAmount(row.disputedAmount)}
        </span>
      ),
    },
    {
      title: 'Raised',
      dataIndex: 'raisedOn',
      hideOnCard: true,
      render: (_, row) => (
        <span className="font-mono-nums text-[13px]">{formatDate(row.raisedOn)}</span>
      ),
    },
    {
      title: 'Open',
      dataIndex: 'daysOpen',
      render: (_, row) => (
        <StatusBadge tone={(row.daysOpen ?? 0) >= 7 ? 'negative' : 'warning'}>
          {row.daysOpen === null ? 'closed' : `${row.daysOpen}d`}
        </StatusBadge>
      ),
    },
    {
      title: '',
      dataIndex: 'id',
      trailing: true,
      render: (_, row) => (
        <div className="flex flex-wrap justify-end gap-2">
          <Button icon="icon icon-clock" onClick={() => setHistory(row)}>
            History
          </Button>
          {/* Closing an argument is the accountant's or a manager's, never the counter's — the
              same split the deposit settlement itself draws. */}
          <WriteAction session={session} required="DepositSettlement:Approve">
            {(disabled) => (
              <Button
                variant="primary"
                icon="icon icon-badge-check"
                disabled={disabled}
                onClick={() => setResolving(row)}
              >
                Close
              </Button>
            )}
          </WriteAction>
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="mb-3 flex flex-wrap items-start justify-between gap-3 lg:mb-6">
        <PageHeading
          title="Disputes"
          subtitle="Where a customer disagreed with what they were charged. It records the argument and never the money — the charge still moves by correcting the inspection."
        />
        <WriteAction session={session} required="ReturnInspection:Edit">
          {(disabled) => (
            <Button
              variant="primary"
              icon="icon icon-flag"
              shortcut="create"
              disabled={disabled}
              onClick={() => setRaising({ bookingId: null })}
            >
              Record a dispute
            </Button>
          )}
        </WriteAction>
      </div>

      {summary.open > 0 && (
        <div className="mb-3 grid gap-3 sm:grid-cols-3 lg:mb-6">
          <Card>
            <div className="text-[13px] text-default">Open</div>
            <div className="font-mono-nums mt-1 text-xl font-semibold text-gray-900">
              {summary.open}
            </div>
            <div className="mt-0.5 text-[13px] text-default">
              {summary.longestOpenDays > 0
                ? `longest ${summary.longestOpenDays} day${summary.longestOpenDays === 1 ? '' : 's'}`
                : 'all raised today'}
            </div>
          </Card>

          <Card>
            <div className="text-[13px] text-default">In question</div>
            <div className="font-mono-nums mt-1 text-xl font-semibold text-gray-900">
              {formatAmount(summary.amountInQuestion)}
            </div>
            {/* Understated on purpose, and saying so beats letting somebody quote it as the total:
                a dispute raised without a figure adds nothing to this. */}
            <div className="mt-0.5 text-[13px] text-default">
              excluding any raised without a figure
            </div>
          </Card>

          <Card>
            <div className="text-[13px] text-default">Waiting a week or more</div>
            <div
              className={`font-mono-nums mt-1 text-xl font-semibold ${
                rows.filter((row) => (row.daysOpen ?? 0) >= 7).length > 0
                  ? 'text-danger'
                  : 'text-gray-900'
              }`}
            >
              {rows.filter((row) => (row.daysOpen ?? 0) >= 7).length}
            </div>
            <div className="mt-0.5 text-[13px] text-default">nobody has answered these</div>
          </Card>
        </div>
      )}

      <Card flush className="p-4 pb-0 max-sm:p-3 max-sm:pb-1">
        <DataView<Dispute>
          columns={columns}
          dataSource={rows}
          loading={open.isLoading}
          rowKey="id"
          empty={
            <EmptyState
              icon="icon icon-flag"
              title="Nothing is being argued about"
              hint="A dispute appears here the moment somebody records that a customer objected to what they were charged."
            />
          }
        />
      </Card>

      {history && (
        <div className="mt-3 lg:mt-6">
          <Card>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <SectionTitle>Everything on {history.customerName}&rsquo;s booking</SectionTitle>
                <p className="mt-1 text-[13px] text-default">
                  Open and closed alike. What was first claimed survives every resolution, which is
                  the first thing anybody reaches for when an argument reopens.
                </p>
              </div>
              <Button icon="icon icon-x" onClick={() => setHistory(null)}>
                Close
              </Button>
            </div>

            {forBooking.isLoading ? (
              <PageLoader compact label="Loading the history" />
            ) : (
              <ul className="mt-3 list-none space-y-2 ps-0">
                {(forBooking.data ?? []).map((entry) => (
                  <li
                    key={entry.id}
                    className="list-none rounded-md border border-border-color p-3 ps-3"
                  >
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <StatusBadge tone={statusTone(entry.status)}>{entry.status}</StatusBadge>
                      <span className="text-[13px] text-default">
                        {formatDateTime(entry.raisedOn)}
                        {entry.raisedByName ? ` · ${entry.raisedByName}` : ''}
                      </span>
                    </div>

                    <p className="mt-2 text-sm whitespace-pre-wrap text-gray-900">{entry.claim}</p>

                    {entry.resolution && (
                      <p className="mt-2 text-[13px] whitespace-pre-wrap text-default">
                        <span className="font-medium text-dark">Resolved: </span>
                        {entry.resolution}
                        {entry.resolvedByName ? ` — ${entry.resolvedByName}` : ''}
                      </p>
                    )}

                    {entry.settledAmount !== null && (
                      <p className="font-mono-nums mt-1 text-[13px] text-default">
                        Settled at {formatAmount(entry.settledAmount)}
                        {entry.disputedAmount !== null &&
                          ` of ${formatAmount(entry.disputedAmount)} claimed`}
                      </p>
                    )}

                    {/*
                      The one thing this log cannot do for itself. Upholding records what was
                      agreed; the charge only moves when the inspection is corrected, so an upheld
                      dispute with no matching correction is a customer told one figure and billed
                      another.
                    */}
                    {needsChargeCorrection(entry) && (
                      <div className="mt-2">
                        <Notice tone="warning" title="Check the charge was actually reduced">
                          This was upheld at a lower figure. Unless somebody corrected the
                          inspection on the returns screen, the customer is still being billed the
                          original.
                        </Notice>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>
      )}

      {raising && (
        <RaiseDisputeDrawer
          branchId={session.branch?.id}
          initialBookingId={raising.bookingId}
          saving={raise.isPending}
          onCommit={(payload) => raise.mutate(payload)}
          onClose={() => setRaising(null)}
        />
      )}

      {resolving && (
        <ResolveDisputeDrawer
          dispute={resolving}
          saving={resolve.isPending}
          onCommit={(payload) => resolve.mutate(payload)}
          onClose={() => setResolving(null)}
        />
      )}
    </>
  );
}
